import { isEmpty, isNil, omit, uniqueId } from 'lodash'
import * as Yup from 'yup'

import {
  DEFAULT_LOOKUP_ENTITY_TYPE,
  DEFAULT_LOOKUP_INSTRUCTIONS,
  DEFAULT_LOOKUP_QUESTION,
  LEGACY_ENTITY_PLACEHOLDER_PROMPT,
  LOOKUP_QUESTION_DEFAULTS,
  LOOKUP_QUESTION_VARIABLES,
  TOOL_CONFIG_VERSION_KEY,
  TOOL_FIELD_FALLBACK_TYPE,
  TOOL_FIELD_KEY_MAX_LENGTH,
  TOOL_REVEALS,
  TOOL_SOURCES,
  TOOL_SPEAKERS,
  TOOL_TYPES,
} from './tools.constants'

export const isLiveToolType = (type) => Object.values(TOOL_TYPES).includes(type)

export const getLookupQuestionDefault = (entityType) =>
  LOOKUP_QUESTION_DEFAULTS[entityType] ?? DEFAULT_LOOKUP_QUESTION

export const isLookupQuestionDefault = (question) => {
  const text = (question || '').trim()
  return (
    isEmpty(text) ||
    [DEFAULT_LOOKUP_QUESTION, ...Object.values(LOOKUP_QUESTION_DEFAULTS)].includes(text)
  )
}

export const toolTypePillProps = (type) =>
  type === TOOL_TYPES.FORM ? { magical: true } : { informative: true }

export const isCardOnlyPrompt = (userPrompt) => {
  const text = (userPrompt || '').trim()
  return !text || text === LEGACY_ENTITY_PLACEHOLDER_PROMPT
}

// Only the web lookup needs a model: the knowledge base answers with agent-gpt's own.
export const needsModel = ({ type, source, user_prompt: userPrompt }) =>
  type === TOOL_TYPES.ENTITY_LOOKUP && source === TOOL_SOURCES.WEB && !isCardOnlyPrompt(userPrompt)

// OpenAI's web search ignores allowed domains on mini and nano models.
export const supportsAllowedUrls = (option) =>
  !/\b(mini|nano)\b/i.test(`${option.name} ${option.label}`)

export const parseAllowedUrls = (text) =>
  text
    .split(',')
    .map((url) => url.trim())
    .filter((url) => !isEmpty(url))

const typeOptionsOfKind = (fieldTypes, kinds) =>
  fieldTypes
    .filter((fieldType) => fieldType.status === 'active' && kinds.includes(fieldType.kind))
    .map((fieldType) => ({ value: fieldType.name, label: fieldType.label }))

export const getValueTypeOptions = (fieldTypes) => typeOptionsOfKind(fieldTypes, ['value', 'both'])

export const getEntityTypeOptions = (fieldTypes) =>
  typeOptionsOfKind(fieldTypes, ['entity', 'both'])

export const typeTakesValues = (fieldTypes, typeName) =>
  Boolean(fieldTypes.find((fieldType) => fieldType.name === typeName)?.takes_values)

export const getFieldTypeLabel = (fieldTypes, typeName) =>
  fieldTypes.find((fieldType) => fieldType.name === typeName)?.label || typeName

// A key the API's pattern accepts ([A-Za-z_][A-Za-z0-9_]{0,47}), derived from a label.
export const slugifyKey = (text, fallback = 'field') => {
  const slug = (text || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, TOOL_FIELD_KEY_MAX_LENGTH)

  if (isEmpty(slug)) {
    return fallback
  }

  return /^[0-9]/.test(slug) ? `_${slug}`.slice(0, TOOL_FIELD_KEY_MAX_LENGTH) : slug
}

export const uniqueKey = (base, taken) => {
  if (!taken.includes(base)) {
    return base
  }

  let counter = 2
  let candidate = base
  while (taken.includes(candidate)) {
    const suffix = `_${counter}`
    candidate = `${base.slice(0, TOOL_FIELD_KEY_MAX_LENGTH - suffix.length)}${suffix}`
    counter += 1
  }

  return candidate
}

// The speaker the row shows by default, saved as such so the API never has to assume it.
export const newToolField = () => ({
  key: '',
  label: '',
  description: '',
  type: '',
  speaker: TOOL_SPEAKERS.CUSTOMER,
  required: false,
  keyLocked: false,
  uiId: uniqueId('tool-field-'),
})

// A label edit regenerates the key until the tool is saved; saved keys never change.
export const applyFieldPatch = (fields, index, patch) =>
  fields.map((field, fieldIndex) => {
    if (fieldIndex !== index) {
      return field
    }

    const next = isNil(patch.type)
      ? { ...field, ...patch }
      : { ...omit(field, 'typeFallbackFrom'), ...patch }

    if (!next.keyLocked && !isNil(patch.label)) {
      const otherKeys = fields
        .filter((_, otherIndex) => otherIndex !== index)
        .map((other) => other.key)
      next.key = uniqueKey(slugifyKey(next.label), otherKeys)
    }

    return next
  })

export const moveItem = (items, from, to) => {
  if (to < 0 || to >= items.length) {
    return items
  }

  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)

  return next
}

// OptionsEditor speaks { name, description }; a pick list's values are { key, label }. The key
// rides along hidden on the option so edits and removals never shift it to another value.
export const valuesToOptions = (values = []) =>
  values.map((value) => ({
    name: String(value.label ?? ''),
    description: value.description || '',
    key: value.key,
    keyLocked: Boolean(value.keyLocked),
  }))

const isLockedOption = (option) => Boolean(option.keyLocked) && !isEmpty(option.key)

export const optionsToValues = (options) => {
  const taken = options.filter(isLockedOption).map((option) => option.key)

  return options.map((option) => {
    const locked = isLockedOption(option)
    const key = locked ? option.key : uniqueKey(slugifyKey(option.name, 'option'), taken)

    if (!locked) {
      taken.push(key)
    }

    return {
      key,
      label: option.name,
      keyLocked: locked,
      ...(isEmpty(option.description) ? {} : { description: option.description }),
    }
  })
}

// Only keys the API has stored are frozen; a row that has not been saved yet keeps following
// its label, including after a round trip through JSON mode.
const savedKeyIndex = (savedConfig) => {
  const savedFields = savedConfig?.fields ?? []

  return {
    fieldKeys: savedFields.map((field) => field.key),
    valueKeys: Object.fromEntries(
      savedFields.map((field) => [field.key, (field.values ?? []).map((value) => value.key)])
    ),
  }
}

const withoutFieldSpeakers = (fields) => fields.map(({ speaker, ...field }) => field)

// A tool-level speaker speaks for every field, so per-field speakers only exist without one.
const withFieldSpeakers = (fields) =>
  fields.map((field) => ({
    ...field,
    speaker: isEmpty(field.speaker) ? TOOL_SPEAKERS.CUSTOMER : field.speaker,
  }))

export const applyToolSpeaker = (config, speaker) =>
  isEmpty(speaker)
    ? { ...omit(config, 'speaker'), fields: withFieldSpeakers(config.fields) }
    : { ...config, speaker, fields: withoutFieldSpeakers(config.fields) }

// Saved rows take a deterministic id so a rebuilt config stays deep-equal and Formik does not
// reinitialize the form; a type the registry no longer offers falls back to free text.
export const toFormConfig = (config, fieldTypes = [], savedConfig = config) => {
  const { [TOOL_CONFIG_VERSION_KEY]: version, fields = [], ...rest } = config || {}
  const availableTypes = getValueTypeOptions(fieldTypes).map((option) => option.value)
  const typeAvailable = (type) =>
    isEmpty(fieldTypes) || isEmpty(type) || availableTypes.includes(type)
  const saved = savedKeyIndex(savedConfig)
  const formFields = fields.map(({ values, ...field }, index) => ({
    ...field,
    keyLocked: !isEmpty(field.key) && saved.fieldKeys.includes(field.key),
    uiId:
      field.uiId || (isEmpty(field.key) ? uniqueId('tool-field-') : `saved-${field.key}-${index}`),
    ...(typeAvailable(field.type)
      ? {}
      : { type: TOOL_FIELD_FALLBACK_TYPE, typeFallbackFrom: field.type }),
    ...(isNil(values)
      ? {}
      : {
          values: values.map((value) => ({
            ...value,
            keyLocked: (saved.valueKeys[field.key] ?? []).includes(value.key),
          })),
        }),
  }))

  return {
    ...rest,
    // A field saved without a speaker shows Customer (ToolFieldRow), so it saves as Customer too.
    fields: isEmpty(rest.speaker)
      ? withFieldSpeakers(formFields)
      : withoutFieldSpeakers(formFields),
  }
}

const stripValue = ({ keyLocked, ...value }) => value

const stripField = ({
  keyLocked,
  uiId,
  typeFallbackFrom,
  values,
  description,
  speaker,
  reveal,
  ...field
}) => ({
  ...field,
  ...(isEmpty(description) ? {} : { description }),
  ...(isEmpty(speaker) ? {} : { speaker }),
  ...(isEmpty(reveal) || reveal === TOOL_REVEALS.FULL ? {} : { reveal }),
  ...(isNil(values) ? {} : { values: values.map(stripValue) }),
})

// The form's config as the API takes it: no UI bookkeeping, and no version (the API sets it).
export const toConfigPayload = (config) => {
  const { [TOOL_CONFIG_VERSION_KEY]: version, fields = [], speaker, ...rest } = config || {}

  return {
    ...rest,
    ...(isEmpty(speaker) ? {} : { speaker }),
    fields: (isEmpty(speaker) ? fields : withoutFieldSpeakers(fields)).map(stripField),
  }
}

export const stringifyConfig = (config) => JSON.stringify(toConfigPayload(config), null, 2)

export const parseConfigJson = (text) => {
  let config

  try {
    config = JSON.parse(text)
  } catch (error) {
    return { error: 'Not valid JSON.' }
  }

  if (isNil(config) || typeof config !== 'object' || Array.isArray(config)) {
    return { error: 'The config must be a JSON object.' }
  }

  if (!Array.isArray(config.fields)) {
    return { error: 'The config needs a "fields" list.' }
  }

  const isObject = (value) => !isNil(value) && typeof value === 'object' && !Array.isArray(value)
  if (!config.fields.every(isObject)) {
    return { error: 'Each field must be an object.' }
  }

  if (
    !config.fields.every(
      (field) =>
        isNil(field.values) || (Array.isArray(field.values) && field.values.every(isObject))
    )
  ) {
    return { error: 'A field\'s "values" must be a list of objects.' }
  }

  return { config }
}

const mapIdsToOptions = (ids = [], options) =>
  ids.map((id) => options.find((option) => option.value === id) || { value: id, label: String(id) })

export const buildToolFormValues = (tool, { agentOptions, playbookOptions, fieldTypes = [] }) => {
  // A card-only Lookup tool's stored prompt is a server placeholder; the editor shows it empty.
  const promptTool =
    tool?.type === TOOL_TYPES.ENTITY_LOOKUP && isCardOnlyPrompt(tool?.prompt?.user_prompt)
      ? null
      : tool
  const isLookup = tool?.type === TOOL_TYPES.ENTITY_LOOKUP
  const isForm = tool?.type === TOOL_TYPES.FORM
  const isNew = isNil(tool)

  return {
    name: tool?.name || '',
    type: tool?.type || '',
    source: tool?.source || TOOL_SOURCES.SHAREPOINT,
    llm_model_id: promptTool?.prompt?.llm_model_id || '',
    user_prompt: promptTool?.prompt?.user_prompt || (isNew ? DEFAULT_LOOKUP_INSTRUCTIONS : ''),
    allowed_urls: (tool?.allowed_urls || []).join(', '),
    entity_type: isLookup ? tool.config?.entities?.[0]?.type || DEFAULT_LOOKUP_ENTITY_TYPE : '',
    lookup_question:
      (isLookup && tool.config?.lookup_question) || (isNew ? DEFAULT_LOOKUP_QUESTION : ''),
    config: isForm ? toFormConfig(tool.config, fieldTypes) : { fields: [] },
    jsonMode: false,
    configJson: '',
    user_whitelist: mapIdsToOptions(tool?.user_whitelist, agentOptions),
    user_blacklist: mapIdsToOptions(tool?.user_blacklist, agentOptions),
    playbook_whitelist: mapIdsToOptions(tool?.playbook_whitelist, playbookOptions),
    playbook_blacklist: mapIdsToOptions(tool?.playbook_blacklist, playbookOptions),
  }
}

// What switching the type starts from, and what switching back to the saved type restores.
export const TYPE_SCOPED_FIELDS = [
  'source',
  'user_prompt',
  'llm_model_id',
  'allowed_urls',
  'entity_type',
  'lookup_question',
  'config',
  'jsonMode',
  'configJson',
]

export const buildToolPayload = (values) => {
  const lists = {
    user_whitelist: values.user_whitelist.map((option) => option.value),
    user_blacklist: values.user_blacklist.map((option) => option.value),
    playbook_whitelist: values.playbook_whitelist.map((option) => option.value),
    playbook_blacklist: values.playbook_blacklist.map((option) => option.value),
  }

  if (values.type === TOOL_TYPES.FORM) {
    return {
      name: values.name,
      type: TOOL_TYPES.FORM,
      config: values.jsonMode
        ? parseConfigJson(values.configJson).config
        : toConfigPayload(values.config),
      ...lists,
    }
  }

  const isWebLookup = values.source !== TOOL_SOURCES.SHAREPOINT
  const lookupQuestion = values.lookup_question.trim()

  return {
    name: values.name,
    type: TOOL_TYPES.ENTITY_LOOKUP,
    source: isWebLookup ? TOOL_SOURCES.WEB : TOOL_SOURCES.SHAREPOINT,
    prompt: {
      model: null,
      feature: 'tool',
      // An empty prompt makes a Lookup tool card only.
      user_prompt: isCardOnlyPrompt(values.user_prompt) ? '' : values.user_prompt,
      // Only while the field shows: a hidden model isn't saved.
      llm_model_id: (needsModel(values) && values.llm_model_id) || null,
      side: 'both',
      max_response_tokens: 1024,
    },
    ...lists,
    allowed_urls: isWebLookup ? parseAllowedUrls(values.allowed_urls) : [],
    config: {
      entities: [{ role: values.entity_type, type: values.entity_type }],
      ...(isEmpty(lookupQuestion) ? {} : { lookup_question: lookupQuestion }),
    },
  }
}

// Where a config error the API reported by path (`fields[2].values`, `entities[0].type`) lands
// in the form: a Lookup tool's entity settings are flat fields, a form's config is nested.
const toFormPath = (path, type) => {
  if (type === TOOL_TYPES.FORM) {
    return `config.${path}`
  }
  if (path.startsWith('entities')) {
    return 'entity_type'
  }
  if (path.startsWith('lookup_question')) {
    return 'lookup_question'
  }
  return `config.${path}`
}

export const getToolFieldErrors = (error, type) => {
  const { errors = [] } = error || {}
  const fieldErrors = {}

  errors.forEach(({ path, message }) => {
    const formPath = toFormPath(path, type)
    if (isNil(fieldErrors[formPath])) {
      fieldErrors[formPath] = message
    }
  })

  return fieldErrors
}

export const formatServerErrors = (error) =>
  (error?.errors || []).map(({ path, message }) => `${path}: ${message}`).join('\n')

const flattenErrorMessages = (errors) => {
  if (isNil(errors)) {
    return []
  }
  if (typeof errors === 'string') {
    return [errors]
  }

  return Object.values(errors).flatMap(flattenErrorMessages)
}

// Config errors that are not about a field row (speaker, groups), prefixed with their key.
export const getConfigErrorMessages = (errors) => {
  if (isNil(errors.config) || typeof errors.config !== 'object') {
    return []
  }

  return Object.entries(errors.config)
    .filter(([key]) => key !== 'fields')
    .flatMap(([key, value]) => flattenErrorMessages(value).map((message) => `${key}: ${message}`))
}

const wordCount = (text) => (text || '').trim().split(/\s+/).filter(Boolean).length

// Mirrors summary-service's _PromptTemplate scan: `${name}` or `$name`, where a bare `$` before a
// digit is a price, not a variable.
export const unknownLookupVariables = (question) => {
  const unknown = []
  const pattern = /\$\{([^}]*)\}|\$([A-Za-z_][A-Za-z0-9_]*)/g
  let match = pattern.exec(question || '')

  while (!isNil(match)) {
    const variable = match[1] ?? match[2]
    if (!LOOKUP_QUESTION_VARIABLES.includes(variable) && !unknown.includes(variable)) {
      unknown.push(variable)
    }
    match = pattern.exec(question || '')
  }

  return unknown
}

export const buildToolValidationSchema = ({ fieldTypes, limits }) => {
  const maxWords = (max) =>
    Yup.string().test('max-words', `At most ${max} words`, (value) => wordCount(value) <= max)

  const valueSchema = Yup.object({
    label: Yup.string().trim().required('Option name is required'),
  })

  const fieldSchema = Yup.object({
    label: maxWords(limits.label_tokens).trim().required('Label is required'),
    description: maxWords(limits.description_tokens),
    type: Yup.string().required('Type is required'),
    values: Yup.array().when('type', {
      is: (type) => typeTakesValues(fieldTypes, type),
      then: (schema) =>
        schema
          .of(valueSchema)
          .min(1, 'Add at least one option')
          .max(limits.values, `A pick list has at most ${limits.values} options`),
      otherwise: (schema) => schema,
    }),
  })

  const fieldsSchema = Yup.array()
    .of(fieldSchema)
    .min(1, 'Add at least one field')
    .max(limits.fields, `A form has at most ${limits.fields} fields`)
    .test('unique-keys', 'Duplicate key', function uniqueKeys(fields = []) {
      const seen = []
      const duplicateIndex = fields.findIndex((field) => {
        const duplicate = seen.includes(field.key)
        seen.push(field.key)
        return duplicate
      })

      if (duplicateIndex === -1) {
        return true
      }

      return this.createError({
        path: `${this.path}[${duplicateIndex}].key`,
        message: `Another field has the key "${fields[duplicateIndex].key}"`,
      })
    })

  return Yup.object().shape({
    name: Yup.string().required('Name is required'),
    type: Yup.string().required('Type is required'),
    // The API builds the prompt from this: a web lookup with instructions needs a model, and
    // without one it fails PromptRequest validation as an unexplained 500.
    llm_model_id: Yup.string().when(['type', 'source', 'user_prompt'], {
      is: (type, source, userPrompt) => needsModel({ type, source, user_prompt: userPrompt }),
      then: (schema) => schema.required('AI model is required'),
      otherwise: (schema) => schema,
    }),
    entity_type: Yup.string().when('type', {
      is: TOOL_TYPES.ENTITY_LOOKUP,
      then: (schema) => schema.required('Entity type is required'),
      otherwise: (schema) => schema,
    }),
    lookup_question: Yup.string().test('known-variables', '', function knownVariables(value) {
      const unknown = unknownLookupVariables(value)
      if (isEmpty(unknown)) {
        return true
      }
      return this.createError({
        message: `Unknown variable ${unknown.map((name) => `\${${name}}`).join(', ')}: use \${name} and \${entity_type}`,
      })
    }),
    config: Yup.mixed().when(['type', 'jsonMode'], {
      is: (type, jsonMode) => type === TOOL_TYPES.FORM && !jsonMode,
      then: () => Yup.object({ fields: fieldsSchema }),
      otherwise: (schema) => schema,
    }),
    configJson: Yup.string().when(['type', 'jsonMode'], {
      is: (type, jsonMode) => type === TOOL_TYPES.FORM && jsonMode,
      then: (schema) =>
        schema.test('valid-json', 'Fix the JSON before saving', (value) =>
          isNil(parseConfigJson(value).error)
        ),
      otherwise: (schema) => schema,
    }),
  })
}

export const getRowErrorMessages = (rowErrors, inlineKeys) => {
  if (isNil(rowErrors) || typeof rowErrors === 'string') {
    return flattenErrorMessages(rowErrors)
  }

  return Object.entries(rowErrors)
    .filter(([key]) => !inlineKeys.includes(key))
    .flatMap(([, value]) => flattenErrorMessages(value))
}
